import { cn } from '@/lib/cn';
import { CheckCircle2, CircleDashed, Loader2, XCircle } from 'lucide-react';

interface SyncStatusBadgeProps {
  label: string;
  syncedAt: string | null;
  error?: string | null;
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 0) return new Date(iso).toLocaleString('ko-KR');
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}초 전`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}일 전`;
  return new Date(iso).toLocaleDateString('ko-KR');
}

export function SyncStatusBadge({ label, syncedAt, error }: SyncStatusBadgeProps) {
  if (error) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs px-2 h-6 rounded-md bg-destructive/10 text-destructive"
        title={error}
      >
        <XCircle className="size-3.5" />
        {label} 실패
      </span>
    );
  }
  if (!syncedAt) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs px-2 h-6 rounded-md bg-surface-muted text-foreground-muted">
        <CircleDashed className="size-3.5" />
        {label} 미실행
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs px-2 h-6 rounded-md bg-[hsl(150_60%_45%)]/10 text-[hsl(150_60%_45%)]">
      <CheckCircle2 className="size-3.5" />
      {label} {formatRelative(syncedAt)}
    </span>
  );
}

export function SyncRunningBadge({ label, className }: { label: string; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-xs px-2 h-6 rounded-md bg-accent/10 text-accent',
        className,
      )}
    >
      <Loader2 className="size-3.5 animate-spin" />
      {label} 진행 중…
    </span>
  );
}
